import type { IFullSyncResult } from '../Types/Docs';

const clip = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max)}…` : s;

const stringify = (v: unknown, max = 400): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return clip(v, max);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return clip(JSON.stringify(v), max);
  } catch {
    return '(unserializable)';
  }
};

/**
 * Parse lastError string (possibly JSON from the server) for display.
 */
export function formatPipelineLastError(lastError: string | null | undefined): string {
  if (lastError == null || String(lastError).trim() === '') {
    return 'The last sync failed (no error detail from server).';
  }
  const t = String(lastError).trim();
  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      const parsed = JSON.parse(t) as Record<string, unknown>;
      const msg =
        typeof parsed.message === 'string'
          ? parsed.message
          : parsed.detail !== undefined && parsed.detail !== null
            ? typeof parsed.detail === 'string'
              ? parsed.detail
              : stringify(parsed.detail, 800)
            : stringify(parsed, 800);
      return clip(typeof msg === 'string' ? msg : String(msg), 1200);
    } catch {
      return clip(t, 1200);
    }
  }
  return clip(t, 1200);
}

/** Build headline + optional detailed lines from SyncResult (counts / errors field). */
export function summarizeLastSyncOutcome(
  lastSync: IFullSyncResult | null | undefined
): { headline: string; supplemental?: string } {
  const syncedAt =
    lastSync?.syncedAt != null && typeof lastSync.syncedAt === 'string'
      ? new Date(lastSync.syncedAt).toLocaleString()
      : null;

  const headline = syncedAt
    ? `Full sync finished (${syncedAt}).`
    : 'Full sync finished.';

  const lines: string[] = [];
  const keys = ['customers', 'caseStudy', 'email', 'hp', 'skipped'] as const;
  for (const k of keys) {
    const v = lastSync?.[k];
    if (v === undefined || v === null) continue;
    if (
      typeof v === 'object' &&
      !Array.isArray(v) &&
      Object.keys(v as object).length === 0
    ) {
      continue;
    }
    lines.push(`${k}: ${stringify(v, 500)}`);
  }

  if (lastSync?.errors !== undefined && lastSync.errors !== null && lastSync.errors !== '') {
    lines.push(`errors: ${stringify(lastSync.errors, 800)}`);
  }

  if (!lines.length) return { headline };

  return {
    headline,
    supplemental: clip(lines.join('\n'), 2800),
  };
}
